import { Component, ElementRef, HostListener, ViewChild } from '@angular/core';
import { AuthenticationManagerOperation } from '../../../../shared/operations/auth/authentication-manager.operation';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { AuthApiService } from '../../../services/auth.api.service';
import { UserState } from '../../../states/user-state';
import { BrowserStorageService } from '../../../services/browser-storage.service';
import { ActivatedRoute, Router } from '@angular/router';
import { finalize } from 'rxjs/operators';
import { userFacingErrorMessage } from '../../../utils/user-facing-error';

type ResetStep = 'password' | 'otp';

@Component({
    selector: 'app-reset-password',
    templateUrl: './reset-password.component.html',
    styleUrl: './reset-password.component.scss',
    standalone: false
})
export class ResetPasswordComponent {
    email: string = '';
    password: string = '';
    keepLoggedIn = false;
    quote: string = '';
    authenticationManager: AuthenticationManagerOperation;
    @ViewChild('emailInput') emailInputElement!: ElementRef;
    fg: FormGroup;
    token: string | null = null;
    isSubmitting = false;
    message: string | null = null;
    error: string | null = null;
    resetStep: ResetStep = 'password';
    adminEmail = '';
    otpSentTo = '';
    sendOtpLoading = false;
    pendingPassword = '';

    constructor(
        private authService: AuthApiService,
        private userState: UserState,
        private router: Router,
        private route: ActivatedRoute,
        private browserStorageService: BrowserStorageService,
        private fb: FormBuilder
    ) {
        this.authenticationManager = new AuthenticationManagerOperation(this.authService, userState, router, browserStorageService);
        this.fg = this.fb.group({
            password1: this.fb.nonNullable.control('', Validators.required),
            password2: this.fb.nonNullable.control('', Validators.required),
            colleagueEmail: this.fb.nonNullable.control('', [Validators.email]),
            adminOtp: this.fb.nonNullable.control('', [Validators.pattern(/^\d{6}$/)]),
        });
        this.token = this.route.snapshot.queryParamMap.get('token');
    }

    ngAfterViewInit() {
        if (this.emailInputElement) {
            this.emailInputElement.nativeElement.focus();
        }
    }

    save(): void {
        this.message = null;
        this.error = null;

        if (!this.token) {
            this.error = 'Missing reset token.';
            return;
        }

        if (this.resetStep === 'password') {
            if (this.fg.get('password1')?.invalid || this.fg.get('password2')?.invalid) {
                this.fg.get('password1')?.markAsTouched();
                this.fg.get('password2')?.markAsTouched();
                return;
            }
            const p1 = String(this.password1Control.value || '');
            const p2 = String(this.password2Control.value || '');
            if (p1 !== p2) {
                this.error = 'Passwords do not match.';
                return;
            }
            this.pendingPassword = p1;
        } else {
            const otp = String(this.adminOtpControl.value || '').trim();
            if (!/^\d{6}$/.test(otp)) {
                this.error = 'Please enter the 6-digit code from another administrator.';
                this.adminOtpControl.markAsTouched();
                return;
            }
            this.isSubmitting = true;
            this.authService
                .resetPassword(this.token, this.pendingPassword, otp)
                .pipe(finalize(() => (this.isSubmitting = false)))
                .subscribe({
                    next: (res) => {
                        this.message = (res as { message?: string })?.message || 'Password reset successfully.';
                        this.router.navigate(['/login']);
                    },
                    error: (err) => {
                        console.error('Reset password failed', err);
                        this.error = userFacingErrorMessage(err, 'resetPassword');
                    },
                });
            return;
        }

        this.isSubmitting = true;
        this.authService
            .resetPassword(this.token, this.pendingPassword)
            .pipe(finalize(() => (this.isSubmitting = false)))
            .subscribe({
                next: (res) => {
                    if (res && 'requiresAdminOtp' in res && res.requiresAdminOtp) {
                        this.adminEmail = res.email || '';
                        this.resetStep = 'otp';
                        this.fg.get('colleagueEmail')?.setValue('');
                        this.fg.get('adminOtp')?.setValue('');
                        return;
                    }
                    this.message = (res as { message?: string })?.message || 'Password reset successfully.';
                    this.router.navigate(['/login']);
                },
                error: (err) => {
                    console.error('Reset password failed', err);
                    this.error = userFacingErrorMessage(err, 'resetPassword');
                },
            });
    }

    sendOtpToColleague(): void {
        this.error = null;
        this.colleagueEmailControl.markAsTouched();
        const colleagueEmail = String(this.colleagueEmailControl.value || '').trim().toLowerCase();
        if (!colleagueEmail || this.colleagueEmailControl.invalid) {
            this.error = 'Please enter a valid colleague email address.';
            return;
        }
        if (colleagueEmail === this.adminEmail) {
            this.error = 'Colleague email must be different from your email.';
            return;
        }
        if (!this.token) return;
        this.sendOtpLoading = true;
        this.authService.requestAdminResetOtp(this.token, colleagueEmail).subscribe({
            next: (res) => {
                this.sendOtpLoading = false;
                this.otpSentTo = res?.email || colleagueEmail;
                this.message = `Code sent to ${this.otpSentTo}. Get the code from them and enter it below.`;
            },
            error: (err) => {
                this.sendOtpLoading = false;
                console.error('Request admin reset OTP failed', err);
                this.error = userFacingErrorMessage(err, 'sendCode');
            },
        });
    }

    get password1Control() {
        return this.fg.get('password1') as FormControl;
    }
    get password2Control() {
        return this.fg.get('password2') as FormControl;
    }
    get colleagueEmailControl() {
        return this.fg.get('colleagueEmail') as FormControl;
    }
    get adminOtpControl() {
        return this.fg.get('adminOtp') as FormControl;
    }

    @HostListener('document:keydown.enter', ['$event'])
    handleEnterKey(_event: KeyboardEvent): void {
        this.save();
    }

    onSaveClick = (_e: Event) => {
        this.save();
    };
}
